import React, { useMemo, useState } from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent, { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Chip from '@mui/material/Chip';
import ButtonBase from '@mui/material/ButtonBase';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import { useColorMode } from '@chakra-ui/react';
import {
  LuRepeat,
  LuCircleCheckBig,
  LuBellRing,
  LuChevronDown,
  LuChevronRight,
  LuPause,
  LuPlay
} from 'react-icons/lu';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';

const iconStyle = { verticalAlign: 'middle', fontSize: 24 };

// Utility: group contiguous reminders only
function groupActivitiesByContiguousReminders(items = []) {
  if (!Array.isArray(items) || items.length === 0) return [];

  const asc = [...items].sort((a, b) => new Date(a.firedTime) - new Date(b.firedTime));
  const out = [];

  let i = 0;
  while (i < asc.length) {
    const curr = asc[i];

    if (curr.type === 'reminder') {
      const group = [];
      while (i < asc.length && asc[i].type === 'reminder') {
        group.push(asc[i]);
        i++;
      }
      if (group.length === 1) {
        out.push(group[0]);
      } else {
        const first = group[0];
        const last = group[group.length - 1];
        out.push({
          id: `reminder-group-${first.firedTime}-${group.length}`,
          type: 'reminderGroup',
          count: group.length,
          first,
          last,
          items: group,
          firedTime: last.firedTime,
          firedTimeUtc: last.firedTimeUtc,
          details: `Reminded ${group.length}×`
        });
      }
      continue;
    }

    out.push(curr);
    i++;
  }

  return out.sort(
    (a, b) =>
      new Date(b.firedTime ?? b.last?.firedTime) -
      new Date(a.firedTime ?? a.last?.firedTime)
  );
}

const ScheduleTimelineDetails = ({ activityData }) => {
  const { colorMode } = useColorMode();
  const muiTheme = useMemo(
    () => createTheme({ palette: { mode: colorMode === 'dark' ? 'dark' : 'light' } }),
    [colorMode]
  );

  const [expandedGroups, setExpandedGroups] = useState({});
  const grouped = useMemo(
    () => groupActivitiesByContiguousReminders(activityData),
    [activityData]
  );

  const toggleGroup = (id) =>
    setExpandedGroups((s) => ({ ...s, [id]: !s[id] }));

  const formatTs = (utc) =>
    new Date(utc).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

const iconStyle = { verticalAlign: 'middle', fontSize: 24 };

const ICON_COLORS = {
  initial: 'magenta',     // LuBellRing
  reminder: 'orange',     // LuRepeat
  completed: 'lime',      // LuCircleCheckBig
  paused: '#ecc94b',
  resumed: '#48bb78',
};
const renderIcon = (activity) => {
  const action = activity.action?.toLowerCase();
  const status = activity.status?.toLowerCase();

  if (action === 'pause' || status === 'paused') {
    return <LuPause style={iconStyle} color={ICON_COLORS.paused} />;
  }
  if (action === 'resume' || status === 'resumed') {
    return <LuPlay style={iconStyle} color={ICON_COLORS.resumed} />;
  }
  if (action === 'complete') {
    return <LuCircleCheckBig style={iconStyle} color={ICON_COLORS.completed} />;
  }
  if (activity.type === 'initial') {
    return <LuBellRing style={{ ...iconStyle, opacity: 0.75 }} color={ICON_COLORS.initial} />;
  }
  if (activity.type === 'reminder' || activity.type === 'reminderGroup') {
    return <LuRepeat style={iconStyle} color={ICON_COLORS.reminder} />;
  }
  return <LuCircleCheckBig style={iconStyle} color={ICON_COLORS.completed} />;
};

const describeActivity = (activity) => {
  const action = activity.action?.toLowerCase();
  if (action === 'pause') return activity.details || 'Paused';
  if (action === 'resume') return activity.details || 'Resumed';
  if (action === 'complete') return activity.details || 'Completed';
  return activity.details;
};


  return (
    <ThemeProvider theme={muiTheme}>
      <Timeline
        sx={{
          [`& .${timelineOppositeContentClasses.root}`]: { flex: 0 },
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          wordBreak: 'break-word',
        }}
      >
        {grouped.map((activity, idx) => {
          const isGroup = activity.type === 'reminderGroup';
          const key = isGroup ? activity.id : activity.firedTime + idx;
          const isLast = idx === grouped.length - 1;

          return (
            <TimelineItem key={key}>
              <TimelineOppositeContent />
              <TimelineSeparator>
                {renderIcon(activity)}
                {!isLast && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent>
                {isGroup ? (
                  <div>
                    {/* Click-anywhere header */}
                    <ButtonBase
                      onClick={() => toggleGroup(activity.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleGroup(activity.id);
                        }
                      }}
                      aria-expanded={!!expandedGroups[activity.id]}
                      sx={(theme) => ({
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'space-between',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        textAlign: 'left',
                        transition: 'background-color 120ms ease',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      })}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Chip
                          size="small"
                          label={`Reminded ${activity.count}×`}
                          color="warning"
                          sx={(theme) => ({
                            // fontWeight: 400,
                            color: theme.palette.warning.dark,
                            bgcolor:
                              colorMode === 'dark'
                                ? alpha(theme.palette.warning.main, 0.33)
                                : alpha(theme.palette.warning.main, 0.33),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                          })}
                        />
                        <Typography
                          variant="body2"
                          sx={(theme) => ({ color: theme.palette.text.secondary })}
                        >
                          {dayjs(activity.first.firedTimeUtc).calendar()} → {dayjs(activity.last.firedTimeUtc).calendar()}
                        </Typography>
                      </span>
                      <span aria-hidden style={{ display: 'flex', alignItems: 'center' }}>
                        {expandedGroups[activity.id] ? <LuChevronDown /> : <LuChevronRight />}
                      </span>
                    </ButtonBase>

                    <Collapse in={!!expandedGroups[activity.id]} timeout="auto" unmountOnExit>
                      <div
                        style={{
                          marginTop: 6,
                          paddingLeft: 8,
                          borderLeft: `1px ${alpha(muiTheme.palette.text.primary, 0.28)}`
                        }}
                      >
                        {activity.items.map((r, i) => (
                          <Typography
                            key={r.firedTime + i}
                            variant="body2"
                            sx={(theme) => ({
                              display: 'block',
                              opacity: 0.95,
                              color: theme.palette.text.secondary,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                            })}
                          ><br/>
                            <span>{dayjs(r.firedTimeUtc).calendar()} • </span>{r.details}
                          </Typography>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                ) : (
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    })}
                  >
                    {describeActivity(activity) || '—'}
                    <span style={{ marginLeft: 8, color: muiTheme.palette.text.secondary }}>
                      {dayjs(activity.firedTimeUtc ?? activity.firedTime).calendar()}
                    </span>
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </ThemeProvider>
  );
};

export default ScheduleTimelineDetails;
